import React, { useState } from 'react'

import {
	Music,
	Volume2,
	VolumeX,
	Calendar,
	ChevronUp,
	RefreshCw,
	Pencil,
	ChevronDown,
} from 'lucide-react'
import { DateTime } from 'luxon'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { ZoneStateStatus } from '@/core/api/entities/zone/state'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePermission } from '@/core/hooks/usePermission'
import {
	useGetZoneConnections,
	useGetZoneOccupation,
} from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ChangeVolumeModal } from '@/features/zones/modals/ChangeVolumeModal'
import { AccountBadge } from '@/shared/components/atoms/AccountBadge/AccountBadge'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Link } from '@/shared/components/atoms/Link'
import ActionDropdownMenu, {
	ActionMenuItem,
} from '@/shared/components/molecules/ActionDropdownMenu'
import { Card } from '@/shared/components/molecules/Card/Card'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { cn, lastSeenText } from '@/shared/utils/helpers'

import { ActivateBadge } from './ActivateBadge'
import { AlertsBadges } from './AlertsBadges'
import { ConnectionChart } from './ConnectionChart'
import IdBadge from './IdBadge'
import { OccupationBarChart } from './OccupationBarChart'
import { useZoneStatus } from '../hooks/useZoneStatus'
import { transformConnectionData } from '../utils/common'

interface ZoneCardProps {
	zone: Zone
	onClick?: () => void
	onUpdateZone?: (data: UpdateZoneRequestDTO) => void
	isUpdating?: boolean
	onRefresh?: (zone: Zone) => void
	onEdit?: (zone: Zone) => void
	onConnectZone?: (zone: Zone) => void
	onManageVolume?: (zone: Zone) => void
	isTrialAccount?: boolean
	/** When true, card is read-only: no zone detail link, no edit/refresh/connect; only "Manage volume" when zone has box */
	sharedLocationView?: boolean
}

export const ZoneCard: React.FC<ZoneCardProps> = ({
	zone,
	onClick,
	onUpdateZone,
	isUpdating = false,
	onRefresh,
	onEdit,
	onConnectZone,
	onManageVolume,
	isTrialAccount = false,
	sharedLocationView = false,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const { features } = usePermission()
	const [isExpanded, setIsExpanded] = useState(false)
	const [chartMode, setChartMode] = useState<'daily' | 'hourly'>('daily')
	const [showVolumeModal, setShowVolumeModal] = useState(false)

	const currentAccount = useCurrentAccount()
	const showAccountBadge =
		!!zone.accountName && zone.accountId !== currentAccount?.id

	const { statusBadges, hasSmartIntegration } = useZoneStatus(zone)

	// Use the same explicit date range as the Zone Details view (last 7 days up to now)
	const endDate = DateTime.now()
	const startDate = endDate.minus({ days: 7 })

	const {
		data: connectionsData,
		isLoading: isLoadingConnections,
		isFetching: isFetchingConnections,
	} = useGetZoneConnections({
		uuid: zone.uuid,
		config: useAccountConfig({
			params: {
				from: startDate.toISODate(),
				to: endDate.toISODate(),
				offset: -(startDate.offset || endDate.offset),
			},
		}),
		options: {
			enabled: isExpanded && !!zone.uuid && !sharedLocationView, // Only fetch when expanded; skip on public shared page
		},
	})

	const { data: occupationData = [], isLoading: isLoadingOccupation } =
		useGetZoneOccupation({
			accountId: zone.accountId,
			zoneId: zone.id,
			config: useAccountConfig({}),
			options: {
				enabled: isExpanded && hasSmartIntegration && !sharedLocationView, // Only fetch for smart zones when expanded; skip on public shared page
			},
		})

	// Transform connection data for the chart (limit to 7 days for daily view, show all for hourly view)
	const connectionData = connectionsData
		? transformConnectionData(
				connectionsData,
				chartMode === 'daily' ? 7 : undefined
			)
		: []

	// Show loading state when expanded but no data yet, or when actively fetching
	const showConnectionsLoading =
		isExpanded &&
		(!connectionsData || isLoadingConnections || isFetchingConnections)

	// Handle volume change
	const handleVolumeChange = (data: UpdateZoneRequestDTO) => {
		if (onUpdateZone) {
			onUpdateZone(data)
			setShowVolumeModal(false)
		}
	}

	const isZoneInstalled =
		zone.state?.status !== ZoneStateStatus.NOT_INSTALLED &&
		zone.state?.status !== ZoneStateStatus.NOT_REACTIVATED

	const shouldShowConnectButton =
		!isZoneInstalled && zone.hasPendingCode && zone.code

	const menuItems: ActionMenuItem[] = sharedLocationView
		? [
				...(zone.getActiveBoxAllocation()
					? [
							{
								label: t('location.monitor.actions.manageVolume'),
								icon: Volume2,
								onClick: () => onManageVolume?.(zone),
								variant: 'default' as const,
							},
						]
					: []),
			]
		: [
				...(isZoneInstalled && features.zone.refresh
					? [
							{
								label: tCommon('entities.zone.actions.refresh'),
								icon: RefreshCw,
								onClick: () => onRefresh?.(zone),
								variant: 'default' as const,
							},
						]
					: []),
				...(onEdit && features.zone.edit
					? [
							{
								label: tCommon('entities.zone.actions.edit'),
								icon: Pencil,
								onClick: () => onEdit(zone),
								variant: 'default' as const,
							},
						]
					: []),
			]

	return (
		<Card
			className={cn(
				'overflow-hidden bg-card-dark border hover:border/10 transition-colors rounded-xl relative',
				isExpanded ?? 'border-b-0'
			)}
		>
			<div className="hidden absolute top-4 right-4 z-10 md:flex items-center gap-3">
				{!sharedLocationView && !isExpanded && (
					<ChevronDown className="h-4 w-4 lg:hidden text-muted-foreground" />
				)}

				{!sharedLocationView && isExpanded && (
					<button
						onClick={(e) => {
							e.stopPropagation()
							setIsExpanded(false)
						}}
						className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 font-medium"
					>
						<span className="hidden lg:block">{t('card.hideDetails')}</span>
						<ChevronUp className="h-4 w-4" />
					</button>
				)}
				{menuItems.length > 0 && <ActionDropdownMenu items={menuItems} />}
			</div>

			<div
				className={cn('px-6 py-5', !sharedLocationView && 'cursor-pointer')}
				onClick={
					sharedLocationView
						? undefined
						: () => {
								if (window.matchMedia('(max-width: 1023px)').matches) {
									onClick?.()
									return
								}
								setIsExpanded(!isExpanded)
							}
				}
			>
				<div className="flex flex-col gap-4">
					<div className="w-full md:w-auto flex items-center justify-between gap-3">
						<div className="flex items-center gap-2 flex-wrap">
							<IdBadge
								id={zone.id}
								boxNumber={zone.getActiveBoxAllocation()?.boxNumber}
							/>

							{zone.hasPendingCode && zone.code && (
								<ActivateBadge
									status={zone.state?.status || ZoneStateStatus.NOT_INSTALLED}
									code={zone.code}
								/>
							)}

							{statusBadges.map((badge, index) => {
								const Icon = badge.icon
								return (
									<Badge
										key={index}
										variant="zones"
										className={cn(
											badge.className,
											'text-[14px] font-medium px-2 py-0.5 h-6'
										)}
									>
										{Icon && <Icon className="h-4 w-4" />}
										{badge.label}
									</Badge>
								)
							})}
						</div>

						{shouldShowConnectButton &&
							onConnectZone &&
							!sharedLocationView && (
								<Button
									variant="noXPadding"
									onClick={(e) => {
										e.stopPropagation()
										onConnectZone(zone)
									}}
									className="md:hidden h-9 rounded-full bg-white text-black hover:bg-gray-100"
								>
									{/* Add translation and translate to Pair - Vincular */}
									Connect
								</Button>
							)}
					</div>

					<div className="flex flex-col lg:flex-row items-start justify-between gap-6">
						<div className="flex-1 min-w-0 flex flex-col gap-1">
							{showAccountBadge && (
								<div className="mb-1">
									<AccountBadge accountName={zone.accountName!} />
								</div>
							)}
							<div className="text-xs text-content-tertiary font-normal leading-[1.4]">
								{zone.location?.name || 'No location'}
							</div>
							<h3
								className="text-xl font-normal line-clamp-1"
								style={{ fontFamily: 'Space Grotesk, sans-serif' }}
							>
								{sharedLocationView ? (
									<span>{zone.name}</span>
								) : (
									<span
										className="cursor-pointer hover:underline"
										onClick={(e) => {
											e.stopPropagation()
											onClick?.()
										}}
									>
										{zone.name}
									</span>
								)}
							</h3>
						</div>

						<div className="flex items-center gap-4">
							<CurrentData zone={zone} />
							{!sharedLocationView &&
								isTrialAccount &&
								isZoneInstalled &&
								onRefresh && (
									<Button
										variant="outline"
										size="sm"
										onClick={(e) => {
											e.stopPropagation()
											onRefresh(zone)
										}}
										className="rounded-full border-border text-sm font-medium whitespace-nowrap"
									>
										{tCommon('entities.zone.actions.refresh')}
									</Button>
								)}
						</div>
					</div>
				</div>
			</div>

			{!sharedLocationView && isExpanded && (
				<div>
					<div className="px-6 pb-5 space-y-4">
						{isZoneInstalled &&
							zone.lastPlayedSchedule &&
							zone?.state?.isOnline() && (
								<div className="space-y-2">
									<div className="text-xs text-content-tertiary font-medium">
										{t('card.nowPlaying')}
									</div>
									<div className="flex items-center gap-3 bg-shade rounded-lg p-3 border border-border">
										<div className="h-10 w-10 bg-shade rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
											{zone.lastPlayedSchedule?.metadata?.smallCovers?.[0] ? (
												<img
													src={zone.lastPlayedSchedule.metadata.smallCovers[0]}
													alt={zone.lastPlayedSchedule.title}
													className="h-full w-full object-cover"
												/>
											) : (
												<Calendar className="h-5 w-5 text-muted-foreground" />
											)}
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-sm font-medium truncate leading-[1.4]">
												<Link to={`/schedules/${zone.lastPlayedSchedule.id}`}>
													{zone.lastPlayedSchedule.title}
												</Link>
											</div>
										</div>
										{zone.getActiveBoxAllocation() && (
											<button
												onClick={(e) => {
													e.stopPropagation()
													setShowVolumeModal(true)
												}}
												className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors font-medium"
											>
												<Volume2 className="h-4 w-4" />
												{t('card.changeVolumeLevel')}
											</button>
										)}
									</div>
								</div>
							)}

						{hasSmartIntegration ? (
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
								<div className="space-y-4">
									<div className="space-y-2">
										<div className="text-xs text-content-tertiary font-medium">
											{t('card.assignedTo')} {zone.groups?.length || 0}{' '}
											{zone.groups?.length !== 1
												? t('card.groups')
												: t('card.group')}
										</div>
										<div className="text-sm font-normal leading-[1.4]">
											{zone.groups && zone.groups.length > 0
												? zone.groups.map((g, i) => (
														<span key={g.id}>
															<Link
																to={`/groups/${g.id}`}
																onClick={(e) => e.stopPropagation()}
																cssVars={{
																	textDecoration: 'none',
																	hoverTextDecoration: 'underline',
																}}
															>
																{g.name}
															</Link>
															{i < zone.groups!.length - 1 && ', '}
														</span>
													))
												: t('card.noGroupsAssigned')}
										</div>
									</div>

									{zone.state?.warnings && zone.state.warnings.length > 0 && (
										<AlertsBadges
											warnings={zone.state.warnings}
											showHeading={true}
											headingClassName="text-xs text-muted-foreground font-medium"
											containerClassName="space-y-2"
										/>
									)}

									<ConnectionChart
										data={connectionData}
										mode={chartMode}
										onModeChange={setChartMode}
										showToggle={true}
										isLoading={showConnectionsLoading}
									/>
								</div>

								<div>
									<OccupationBarChart
										occupationData={occupationData}
										isLoading={isLoadingOccupation}
										integrationSource={zone.integrationSource}
										integrationSourcePretty={zone.integrationSourcePretty}
										showCard={true}
									/>
								</div>
							</div>
						) : (
							<div
								className={cn(
									'grid grid-cols-1 gap-4',
									zone.state?.warnings && zone.state.warnings.length > 0
										? 'sm:grid-cols-3'
										: 'sm:grid-cols-2'
								)}
							>
								<div className="space-y-2">
									<div className="text-xs text-content-tertiary font-medium">
										{t('card.assignedTo')} {zone.groups?.length || 0}{' '}
										{zone.groups?.length !== 1
											? t('card.groups')
											: t('card.group')}
									</div>
									<div className="text-sm font-normal leading-[1.4]">
										{zone.groups && zone.groups.length > 0
											? zone.groups.map((g, i) => (
													<span key={g.id}>
														<Link
															to={`/groups/${g.id}`}
															onClick={(e) => e.stopPropagation()}
															cssVars={{
																textDecoration: 'none',
																hoverTextDecoration: 'underline',
															}}
														>
															{g.name}
														</Link>
														{i < zone.groups!.length - 1 && ', '}
													</span>
												))
											: t('card.noGroupsAssigned')}
									</div>
								</div>

								{/* Alerts - only render if there are warnings */}
								{zone.state?.warnings && zone.state.warnings.length > 0 && (
									<AlertsBadges
										warnings={zone.state.warnings}
										showHeading={true}
										headingClassName="text-xs text-muted-foreground font-medium"
										containerClassName="space-y-2"
									/>
								)}

								{/* Connection chart (compressed) */}
								<div>
									{connectionData.length > 0 || showConnectionsLoading ? (
										<ConnectionChart
											data={connectionData}
											mode={chartMode}
											onModeChange={setChartMode}
											showToggle={true}
											isLoading={showConnectionsLoading}
										/>
									) : (
										<div className="space-y-2">
											<div className="text-xs text-muted-foreground font-medium">
												{t('card.connectionFromLast7Days')}
											</div>
											<div className="text-sm text-white/60">
												{t('card.noConnectionData')}
											</div>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Volume Modal - only when not in shared location view (parent handles volume via onManageVolume) */}
			{!sharedLocationView && (
				<ChangeVolumeModal
					visible={showVolumeModal}
					zone={zone}
					onClose={() => setShowVolumeModal(false)}
					onConfirm={handleVolumeChange}
					isUpdating={isUpdating}
				/>
			)}
		</Card>
	)
}

interface CurrentDataProps {
	zone: Zone
}

const CurrentData = ({ zone }: CurrentDataProps) => {
	const { t } = useTranslation('zones')

	// Get playback status
	const getPlaybackStatus = () => {
		const state = zone.state?.state
		const volume = zone.state?.volume ?? zone.volume

		if (!state) {
			return {
				icon: VolumeX,
				text: t('card.playbackStatus.scheduledSilence'),
			}
		}

		switch (state) {
			case 'playing':
				return {
					icon: Music,
					text: t('card.playbackStatus.playing'),
					volume: volume,
				}
			case 'waiting':
				return {
					icon: Calendar,
					text: t('card.playbackStatus.scheduledPlaying'),
					volume: volume,
				}
			case 'downloading':
				return {
					icon: VolumeX,
					text: t('card.playbackStatus.downloading'),
				}
			default:
				return {
					icon: VolumeX,
					text: t('card.playbackStatus.scheduledSilence'),
				}
		}
	}

	const playbackStatus = getPlaybackStatus()
	const PlaybackIcon = playbackStatus.icon

	const lastSeen = zone.state?.lastSeen

	if (!zone.state?.isOnline()) {
		return null
	}

	return (
		<div className="flex flex-col gap-2 min-w-[290px] pt-1">
			{/* Last seen */}
			<div className="text-xs text-content-tertiary font-medium leading-[1.4]">
				{zone.state?.status === ZoneStateStatus.OFFLINE && !!lastSeen
					? t('card.lastSeen') + ': ' + lastSeenText(lastSeen)
					: zone.state?.status === ZoneStateStatus.NOT_INSTALLED
						? ''
						: zone.state?.status === ZoneStateStatus.NOT_REACTIVATED
							? ''
							: t('card.playback')}
			</div>

			{/* Playback status */}
			<div className="flex items-center gap-2 text-sm">
				{zone.state?.status !== ZoneStateStatus.NOT_INSTALLED &&
				zone.state?.status !== ZoneStateStatus.NOT_REACTIVATED ? (
					<>
						<div className="flex items-center gap-2">
							<PlaybackIcon className="h-[18px] w-[18px]" />
							<span className="font-normal leading-[1.4]">
								{playbackStatus.text}
							</span>
						</div>
						{playbackStatus.volume !== undefined && (
							<>
								<div className="w-1 h-1 rounded-full bg-foreground" />
								<div className="flex items-center gap-1">
									<Volume2 className="h-4 w-4" />
									<span className="font-normal leading-[1.4]">
										{t('card.volume')}: {playbackStatus.volume}
									</span>
								</div>
							</>
						)}
					</>
				) : (
					// Empty div to keep the height consistent if needed, or nothing.
					// Based on request: "If not installed or not reactivated, no Icons."
					<span className="font-normal leading-[1.4]">
						{/* Could put some text here or just leave empty as requested */}
					</span>
				)}
			</div>
		</div>
	)
}
